import { Injectable } from '@angular/core';
import { TaxillaApiService } from '@encomply-core/services/taxillaapi/taxillaapi.service';
import { translate } from '@ngneat/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, delay, from, map, mergeMap, of, withLatestFrom } from 'rxjs';

import { MastersMetaData } from '../../models/masters/mastersmetadata.class';
import { MasterRecordsResponse, MastersResponseRecord } from '../../interface/master-records-response.interface';
import { ChangeLog } from '../../interface/record-change-log.interface';
import { SearchFilter } from '../../models/searchfilter.class';
import { ApiService } from '../../services/api/api.service';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import { InstancesService } from '../../services/instances/instances.service';
import { MastersService } from '../../services/masters/masters.service';
import { UtilsService } from '../../services/utils/utils.service';
import * as masterActions from '../actions';
import { AlertError } from '../actions';
import { SetMasterChainDisplayNames } from '../actions/master-management.actions';
import {
    getCurrentOrganizationId$,
    getIsRecordsLoading$,
    getMasterchainDisplayNames$,
    getMasterLanguageTemplate$,
    getMasterRecordPagination$,
    getMasterRecords$,
    getMasterRequests$,
    getMasters$,
    getMastersInboundMappings$,
    getMastersMetaDataMap$,
    getMastersOutboundMappings$,
    getMasterUploadFiles$,
    getParentMasterRecords$,
    getParentPrimaryKeys$,
    getReportDetails$,
    getSearchFilters$,
    getSelectedMasterKeys$,
    getSelectedRestApiName$,
    getSelectedRootOrganization$,
    getSelectedTableId$,
} from '../selectors';

@Injectable()
export class MasterEffects {
    private loadingMap: { [property: string]: boolean } = {};

    constructor(
        private store$: Store,
        private actions$: Actions,
        private _taxilla: TaxillaApiService,
        private _masters: MastersService,
        private _instances: InstancesService,
        private _utils: UtilsService,
        private _api: ApiService
    ) {}
    getMasterData$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(masterActions.GetMasters),
            withLatestFrom(this.store$.select(getMasters$), this.store$.select(getCurrentOrganizationId$)),
            mergeMap(([_action, masters, currentOrganizationId]) => {
                if (masters?.length > 0 || !currentOrganizationId || currentOrganizationId.length === 0) {
                    return [];
                }
                return this._taxilla.masters.getAllMasters(currentOrganizationId).pipe(
                    map(res => {
                        const mastersList = Object.values(res);
                        return masterActions.GetMastersSuccess({
                            masters: mastersList || [],
                            organizationId: currentOrganizationId,
                        });
                    }),
                    catchError(errorResponse => of(AlertError({ message: errorResponse?.msg })))
                );
            })
        );
    });

    getMasterMetadata$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(masterActions.GetMasterMetadata),
            withLatestFrom(this.store$.select(getMastersMetaDataMap$), this.store$.select(getCurrentOrganizationId$)),
            mergeMap(([action, metaDataMap, currentOrganizationId]) => {
                if (!action.tableId) {
                    return [];
                }
                const masterMetaData = metaDataMap?.[action.tableId] || undefined;
                if (masterMetaData || this.loadingMap[action.tableId]) {
                    return [];
                }
                this.loadingMap[action.tableId] = true;
                return this._masters.getMasterMetaDataObservable(action).pipe(
                    map((res: MastersMetaData) => {
                        this.loadingMap[action.tableId] = false;
                        const missingTableId = this.getMissingTableIdFromMetaMap(metaDataMap, res?.masterMetadata?.parentTableId);
                        if (missingTableId?.length > 0) {
                            this.store$.dispatch(
                                masterActions.GetMasterMetadata({
                                    tableId: missingTableId,
                                    organizationId: currentOrganizationId,
                                    isParent: true,
                                })
                            );
                        }
                        return masterActions.SetMasterMetadata({
                            metaData: res,
                            organizationId: currentOrganizationId,
                        });
                    }),
                    catchError(errorResponse => {
                        this.loadingMap[action.tableId] = false;
                        return of(AlertError({ message: errorResponse?.msg }));
                    })
                );
            })
        );
    });

    getMastersLanaguageTemplate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(masterActions.GetMasterLanguageTemplate),
            withLatestFrom(this.store$.select(getSelectedRestApiName$), this.store$.select(getMasterLanguageTemplate$)),
            mergeMap(([action, restApiName, masterTemplate]) => {
                if (!action.tableId) {
                    return [];
                }
                if (masterTemplate) {
                    return [];
                }
                return from(this._masters.getMasterLanguageTemplateObservable({ ...action, restApiName })).pipe(
                    map(response => {
                        return masterActions.SetMasterLanguageTemplate({
                            masterLanguageTemplate: response,
                            organizationId: action.organizationId,
                            restApiName: restApiName,
                        });
                    }),
                    catchError(errorResponse => {
                        if (errorResponse?.msg) {
                            return of(AlertError({ message: errorResponse?.msg }));
                        } else {
                            return of(
                                masterActions.SetMasterLanguageTemplate({
                                    masterLanguageTemplate: {},
                                    organizationId: action.organizationId,
                                    restApiName: restApiName,
                                })
                            );
                        }
                    })
                );
            })
        );
    });

    getCustomSearchFilters$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(masterActions.GetCustomFilters),
            withLatestFrom(
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getSelectedRestApiName$),
                this.store$.select(getSearchFilters$),
                this.store$.select(getSelectedTableId$)
            ),
            mergeMap(([_action, currentOrganizationId, restApiName, searchFilters, tableId]) => {
                if (searchFilters?.length > 0) {
                    return [];
                }
                return this._instances.getSearchConfigurationObservable(tableId).pipe(
                    map((response: SearchFilter[]) => {
                        response.forEach(filter => {
                            filter['filterType'] = 'custom';
                            filter['value'] = JSON.parse(filter.criteria);
                            filter['searchId'] = filter.objectId;
                        });
                        return masterActions.SetCustomFilters({
                            searchFilters: response,
                            organizationId: currentOrganizationId,
                            restApiName: restApiName,
                        });
                    }),
                    catchError(errorResponse => of(AlertError({ message: errorResponse?.msg })))
                );
            })
        );
    });

    private getMissingTableIdFromMetaMap = (
        metdataMap: {
            [property: string]: MastersMetaData;
        },
        tableId: string
    ) => {
        if (!tableId) {
            return undefined;
        }
        if (!metdataMap?.[tableId]) {
            return tableId;
        }
        if (metdataMap?.[tableId]?.masterMetadata?.parentTableId) {
            return this.getMissingTableIdFromMetaMap(metdataMap, metdataMap?.[tableId]?.masterMetadata?.parentTableId);
        }
        return undefined;
    };

    private generateId = (records: MastersResponseRecord[], primaryKeys: string[]) => {
        records.forEach(record => {
            const primaryKeysValues = [];
            primaryKeys.forEach(key => {
                primaryKeysValues.push(record.fields?.find(field => field.id === key)?.value);
            });
            record['recordId'] = primaryKeysValues?.join('#');
        });
    };

    getMasterRecords$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(masterActions.GetMasterRecords),
            withLatestFrom(
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getSelectedRestApiName$),
                this.store$.select(getMasterRecords$),
                this.store$.select(getMastersMetaDataMap$),
                this.store$.select(getIsRecordsLoading$),
                this.store$.select(getSelectedMasterKeys$)
            ),
            mergeMap(([action, currentOrganizationId, selectedRestApiName, records, metaDataMap, loader, primaryKeys]) => {
                if (!selectedRestApiName) {
                    return [];
                }
                if (records?.length > 0 && records.length === action.size) {
                    return [];
                }
                if ((!action.pagingState || action.pagingState.length === 0) && action.pageIndex !== 0) {
                    return [];
                }
                if (loader) {
                    return [];
                }
                let metaData: MastersMetaData;
                Object.keys(metaDataMap).forEach(key => {
                    if (metaDataMap[key]?.masterItem?.restApiServiceName === selectedRestApiName) {
                        metaData = metaDataMap[key];
                    }
                });
                if (!metaData) {
                    setTimeout(() => {
                        this.store$.dispatch(masterActions.GetMasterRecords(action));
                    }, 200);
                    return [];
                }
                const missingTableId = this.getMissingTableIdFromMetaMap(metaDataMap, metaData?.masterItem?.itemId);
                if (missingTableId?.length > 0) {
                    this.store$.dispatch(
                        masterActions.GetMasterMetadata({ tableId: missingTableId, organizationId: currentOrganizationId, isParent: true })
                    );
                    setTimeout(() => {
                        this.store$.dispatch(masterActions.GetMasterRecords(action));
                    }, 200);
                    return [];
                }
                this.store$.dispatch(masterActions.setRecordsLoading({ loading: true }));
                return this._masters
                    .getRecords({
                        size: action.size,
                        pagingState: action.pagingState,
                        restApiName: selectedRestApiName,
                    })
                    .pipe(
                        map((res: MasterRecordsResponse) => {
                            this.store$.dispatch(masterActions.setRecordsLoading({ loading: false }));
                            const records = res?.records || [];
                            this.generateId(records, primaryKeys);
                            this.store$.dispatch(
                                masterActions.SetMasterRecordPagination({
                                    organizationId: currentOrganizationId,
                                    restApiName: selectedRestApiName,
                                    pageSize: action.size,
                                    pageIndex: action.pageIndex,
                                    pageState: res?.pagingState,
                                    search: {},
                                    isUpdate: true,
                                })
                            );
                            return masterActions.SetMasterRecords({
                                records: records,
                                organizationId: currentOrganizationId,
                                restApiName: selectedRestApiName,
                                isClearSearch: false,
                            });
                        }),
                        catchError(errorResponse => {
                            this.store$.dispatch(masterActions.setRecordsLoading({ loading: false }));
                            return of(AlertError({ message: errorResponse?.msg }));
                        })
                    );
            })
        );
    });

    getParentMasterRecords$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(masterActions.GetParentMasterRecords),
            withLatestFrom(
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getParentMasterRecords$),
                this.store$.select(getSelectedMasterKeys$)
            ),
            mergeMap(([action, currentOrganizationId, records, primaryKeys]) => {
                if (records?.length > 0) {
                    return of(
                        masterActions.SetMasterRecords({
                            records: records,
                            organizationId: currentOrganizationId,
                            restApiName: action.restApiName,
                            isClearSearch: false,
                        })
                    );
                }
                return this._masters
                    .getRecords({
                        size: action.size,
                        pagingState: action.pagingState,
                        restApiName: action.restApiName,
                        noAlerts: action.noAlerts,
                    })
                    .pipe(
                        map((res: MasterRecordsResponse) => {
                            if (res?.pagingState?.length > 0) {
                                this.store$.dispatch(
                                    masterActions.GetParentMasterRecords({
                                        size: action.size,
                                        pagingState: res?.pagingState,
                                        restApiName: action.restApiName,
                                    })
                                );
                            }
                            const records = res?.records || [];
                            this.generateId(records, primaryKeys);
                            return masterActions.SetMasterRecords({
                                records: records,
                                organizationId: currentOrganizationId,
                                restApiName: action.restApiName,
                                isClearSearch: false,
                            });
                        }),
                        catchError(errorResponse => of(AlertError({ message: errorResponse?.msg })))
                    );
            })
        );
    });

    getSearchedMasterRecords$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(masterActions.GetSearchedMasterRecords),
            withLatestFrom(
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getSelectedRootOrganization$),
                this.store$.select(getMasterRecordPagination$),
                this.store$.select(getSelectedTableId$),
                this.store$.select(getSelectedRestApiName$),
                this.store$.select(getMasterRecords$),
                this.store$.select(getIsRecordsLoading$),
                this.store$.select(getSelectedMasterKeys$)
            ),
            mergeMap(([action, orgId, rootOrg, pagination, masterId, restApiName, records, loader, primaryKeys]) => {
                const tableUid = action?.tableUid || masterId;
                const masterRestApiName = action.restApiName || restApiName;
                const masterPagination = action.pagination || pagination;
                const masterPrimaryKeys = action.primaryKeys || primaryKeys;
                if (restApiName && records?.length > 0) {
                    return [];
                }
                if (loader) {
                    return [];
                }
                this.store$.dispatch(masterActions.setRecordsLoading({ loading: true }));
                const payload = {
                    organizationId: rootOrg?.id || orgId,
                    payload: {
                        queryString: masterPagination?.search?.queryString,
                        size: masterPagination?.pageSize,
                        from: masterPagination?.pageIndex ? masterPagination?.pageIndex * masterPagination?.pageSize : 0,
                        masterId: tableUid,
                        unitId: orgId,
                        sort: masterPagination?.search?.sortQuery,
                    },
                };
                return this._masters.searchMastersObservable(payload).pipe(
                    map((res: any) => {
                        this.generateId(res?.response?.masterRecords || [], masterPrimaryKeys);
                        this.store$.dispatch(masterActions.setRecordsLoading({ loading: false }));
                        this.store$.dispatch(
                            masterActions.SetMasterRecordPagination({
                                organizationId: orgId,
                                restApiName: masterRestApiName,
                                pageSize: masterPagination.pageSize,
                                pageIndex: masterPagination.pageIndex,
                                pageState: '',
                                search: {
                                    searchAfter: res?.response?.records?.searchAfter,
                                },
                                isUpdate: true,
                                tableUid,
                            })
                        );
                        return masterActions.SetMasterRecords({
                            records: res?.response?.masterRecords,
                            organizationId: orgId,
                            restApiName: masterRestApiName,
                            isClearSearch: false,
                        });
                    }),
                    catchError(errorResponse => {
                        this.store$.dispatch(masterActions.setRecordsLoading({ loading: false }));
                        return of(AlertError({ message: errorResponse?.msg }));
                    })
                );
            })
        );
    });

    getSearchedMasterRecordsCount$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(masterActions.GetSearchedMasterRecordsCount),
            withLatestFrom(
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getSelectedRootOrganization$),
                this.store$.select(getMasterRecordPagination$),
                this.store$.select(getSelectedTableId$)
            ),
            mergeMap(([_action, orgId, rootOrg, pagination, masterId]) => {
                const payload = {
                    organizationId: rootOrg?.id,
                    payload: {
                        queryString: pagination?.search?.queryString,
                        masterId: masterId,
                        unitId: orgId,
                        sort: pagination?.search?.sortQuery,
                    },
                };
                return this._masters.getSearchMasterRecordsCountObservable(payload).pipe(
                    map((res: any) => {
                        return masterActions.SetSearchedRecordsCount({
                            searchRecordsCount: res?.response?.count,
                        });
                    }),
                    catchError(errorResponse => of(AlertError({ message: errorResponse?.msg })))
                );
            })
        );
    });

    CreateMasterRecord$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(masterActions.CreateRecord),
            withLatestFrom(
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getSelectedRestApiName$),
                this.store$.select(getSelectedMasterKeys$)
            ),
            mergeMap(([action, orgId, selectedRestApiName, primaryKeys]) => {
                return this._masters.addRecord(action.data).pipe(
                    map((res: any) => {
                        const records = [res?.response];
                        this.generateId(records, primaryKeys);
                        this.store$.dispatch(masterActions.CRUDOperationRecord({ actionType: 'CREATE' }));
                        return masterActions.SetMasterRecords({
                            records: records,
                            organizationId: orgId,
                            restApiName: selectedRestApiName,
                        });
                    }),
                    catchError(errorResponse => of(AlertError({ message: errorResponse?.msg })))
                );
            })
        );
    });

    UpdateMasterRecord$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(masterActions.UpdateMasterRecord),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$), this.store$.select(getSelectedRestApiName$)),
            mergeMap(([action, orgId, selectedRestApiName]) => {
                return this._masters.updateRecord(action.data).pipe(
                    map((res: any) => {
                        const record = res?.response;
                        delete record.businessKey;
                        record['recordId'] = action.recordId;
                        this.store$.dispatch(masterActions.CRUDOperationRecord({ actionType: 'UPDATE' }));
                        return masterActions.UpdateMasterRecordSuccess({
                            record: record,
                            organizationId: orgId,
                            restApiName: selectedRestApiName,
                        });
                    }),
                    catchError(errorResponse => of(AlertError({ message: errorResponse?.msg })))
                );
            })
        );
    });

    DeleteMasterRecords$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(masterActions.DeleteMasterRecords),
            withLatestFrom(
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getSelectedRestApiName$),
                this.store$.select(getSelectedTableId$)
            ),
            mergeMap(([action, orgId, restApiName, tableId]) => {
                return this._masters.deleteMastersRecords({ masterDataRowKeys: action.data, masterRestApiName: restApiName }).pipe(
                    map(() => {
                        this.store$.dispatch(
                            masterActions.SetMasterRecords({
                                records: [],
                                organizationId: orgId,
                                restApiName: restApiName,
                                isClearSearch: true,
                            })
                        );
                        this.store$.dispatch(masterActions.DeleteParentRelatedChildRecords({ tableId, orgId }));
                        return masterActions.CRUDOperationRecord({ actionType: 'DELETE' });
                    }),
                    catchError(errorResponse => of(AlertError({ message: errorResponse?.msg })))
                );
            })
        );
    });

    getParentPrimaryKeys$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(masterActions.GetParentPrimaryKeys),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$), this.store$.select(getParentPrimaryKeys$)),
            mergeMap(([action, orgId, primaryKeys]) => {
                if (primaryKeys?.keyColumns?.length > 0) {
                    return of(
                        masterActions.SetParentPrimaryKeys({
                            organizationId: orgId,
                            tableId: action.tableId,
                            primaryKeys: primaryKeys?.keyColumns,
                        })
                    );
                }
                return this._masters.getParentPrimaryKeysObservable(action).pipe(
                    map((res: any) => {
                        this.store$.dispatch(
                            masterActions.SetParentTableId({
                                parentTableId: action.tableId,
                            })
                        );
                        return masterActions.SetParentPrimaryKeys({
                            organizationId: orgId,
                            tableId: action.tableId,
                            primaryKeys: res?.response?.parentPrimaryKeyColumns,
                        });
                    }),
                    catchError(errorResponse => of(AlertError({ message: errorResponse?.msg })))
                );
            })
        );
    });

    GetMastersInboundMappings$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(masterActions.GetMastersInboundMappings),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$), this.store$.select(getMastersInboundMappings$)),
            mergeMap(([action, orgId, inboundMap]) => {
                if (inboundMap?.mappings) {
                    return of(
                        masterActions.SetMastersInboundMappings({
                            organizationId: orgId,
                            tableId: action.masterTableId,
                            inboundMappings: CommonUtilsService.cloneObject(inboundMap),
                        })
                    );
                }
                return this._masters
                    .getMastersInboundMappingsObservable({ organizationId: orgId, masterTableId: action.masterTableId })
                    .pipe(
                        map((res: any) => {
                            return masterActions.SetMastersInboundMappings({
                                organizationId: orgId,
                                tableId: action.masterTableId,
                                inboundMappings: res?.response,
                            });
                        }),
                        catchError(errorResponse => of(AlertError({ message: errorResponse })))
                    );
            })
        );
    });

    GetChainDisplayNames = createEffect(() =>
        this.actions$.pipe(
            ofType(masterActions.GetMasterChainDisplayNames),
            delay(10),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$), this.store$.select(getMasterchainDisplayNames$)),
            mergeMap(([action, organizationId, chainDisplayNames]) => {
                if (chainDisplayNames?.length > 0) {
                    return [];
                }
                return from(
                    this._api.assets.getChainDisplayNames({
                        restApiName: action.restAPIName,
                    })
                ).pipe(
                    map(res => {
                        return SetMasterChainDisplayNames({
                            restAPIName: action.restAPIName,
                            organizationId,
                            chainDisplayNames: res?.chainNameVsChainDisplayName,
                        });
                    }),
                    catchError(errorResponse => of(AlertError({ message: errorResponse })))
                );
            })
        )
    );

    GetMasterRequests$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(masterActions.GetMasterRequests),
            withLatestFrom(
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getSelectedRestApiName$),
                this.store$.select(getMasterRequests$)
            ),
            mergeMap(([action, currentOrganizationId, selectedRestApiName, requestsMap]) => {
                if (!selectedRestApiName) {
                    return [];
                }
                if (requestsMap?.requests?.length > 0) {
                    return [];
                }
                return this._masters.getMasterRequestsObservable(action).pipe(
                    map(res => {
                        this.store$.dispatch(
                            masterActions.SetMasterRequestsPagination({
                                organizationId: currentOrganizationId,
                                restApiName: selectedRestApiName,
                                pageSize: action.size,
                                pageIndex: action.pageIndex,
                                pageState: res?.nextPagingState,
                                timeRange: [],
                                isUpdate: true,
                            })
                        );
                        return masterActions.SetMasterRequests({
                            requests: res?.assetRequests,
                            organizationId: currentOrganizationId,
                            restApiName: action.restApiName,
                        });
                    }),
                    catchError(errorResponse => {
                        return of(AlertError({ message: errorResponse?.msg }));
                    })
                );
            })
        );
    });

    GetMastersOutboundMappings$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(masterActions.GetMastersOutboundMappings),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$), this.store$.select(getMastersOutboundMappings$)),
            mergeMap(([action, orgId]) => {
                return this._masters.getOutboundMappings({ organizationId: orgId, tableId: action.masterTableId }).pipe(
                    map((res: any) => {
                        return masterActions.SetMastersOutboundMappings({
                            organizationId: orgId,
                            tableId: action.masterTableId,
                            outboundMappings: res,
                        });
                    }),
                    catchError(errorResponse => of(AlertError({ message: errorResponse })))
                );
            })
        );
    });

    saveSearchCriteria$ = createEffect(() =>
        this.actions$.pipe(
            ofType(masterActions.SaveSearchConfiguration),
            withLatestFrom(
                this.store$.select(getSearchFilters$),
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getSelectedRestApiName$)
            ),
            mergeMap(([action, searchMap, orgId, restApiName]) => {
                const searchFilters = searchMap || [];
                return this._api.instances
                    .saveSearchConfiguration({
                        criteria: action.criteria,
                        filterName: action.filterName,
                        objectId: action.objectId,
                        id: action.id,
                    })
                    .pipe(
                        map((res: any) => {
                            this.store$.dispatch(masterActions.SavedSearchFilter({ filterObject: res?.response }));
                            return masterActions.SetCustomFilters({
                                searchFilters: [res?.response, ...searchFilters.filter(filter => filter.id !== action.id)],
                                organizationId: orgId,
                                restApiName: restApiName,
                            });
                        }),
                        catchError(errorResponse => of(AlertError({ message: errorResponse })))
                    );
            })
        )
    );

    deleteSearchConfiguration$ = createEffect(() =>
        this.actions$.pipe(
            ofType(masterActions.DeleteSearchConfiguration),
            withLatestFrom(
                this.store$.select(getSearchFilters$),
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getSelectedRestApiName$)
            ),
            mergeMap(([action, searchMap, orgId, restApiName]) => {
                const searchFilters = searchMap || [];
                return this._api.instances
                    .deleteSearchConfiguration({
                        id: action?.id,
                    })
                    .pipe(
                        map((res: any) => {
                            this.store$.dispatch(
                                masterActions.AlertSuccess({ message: res?.msg || translate('Filter deleted successfully') })
                            );
                            return masterActions.SetCustomFilters({
                                searchFilters: searchFilters.filter(filter => filter?.id !== action.id),
                                organizationId: orgId,
                                restApiName: restApiName,
                            });
                        }),
                        catchError(errorResponse => of(AlertError({ message: errorResponse })))
                    );
            })
        )
    );

    GetReportDetails$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(masterActions.GetReportDetails),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$), this.store$.select(getReportDetails$)),
            mergeMap(([action, orgId, reportDetails]) => {
                if (reportDetails !== undefined && !action?.isFromGenerate) {
                    return [];
                }
                return this._masters.getReportDetails({ organizationId: orgId, tableId: action.tableId, noAlerts: action?.noAlerts }).pipe(
                    map((res: any) => {
                        return masterActions.SetReportDetails({
                            organizationId: orgId,
                            tableId: action.tableId,
                            reportDetails: res,
                        });
                    }),
                    catchError(errorResponse => of(AlertError({ message: errorResponse })))
                );
            })
        );
    });

    generateReport$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(masterActions.GenerateMasterReport),
            mergeMap(action => {
                return this._masters
                    .generateMasterReport({
                        transformationName: action.transformationName,
                        restApiName: action.restApiName,
                        noAlerts: action?.noAlerts,
                    })
                    .pipe(
                        map((res: any) => {
                            this.store$.dispatch(masterActions.AlertSuccess({ message: res?.msg }));
                            return masterActions.GeneratedMasterReport({
                                masterId: action.masterId,
                                serviceId: action.serviceId,
                                repositoryId: action.repositoryId,
                                chainName: action.chainName,
                                transformationName: action.transformationName,
                                restApiName: action.restApiName,
                                timeStamp: action.timeStamp,
                            });
                        }),
                        catchError(errorResponse => {
                            return of(
                                masterActions.GenerateMasterReportFailure({
                                    msg: errorResponse?.msg,
                                    transformationName: action?.transformationName,
                                })
                            );
                        })
                    );
            })
        );
    });

    private getfileName = name => {
        let checkSumFileName = name.split('/');
        checkSumFileName = checkSumFileName[checkSumFileName.length - 1];
        return checkSumFileName;
    };

    GetMasterUploadFiles$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(masterActions.GetMasterUploadFiles),
            withLatestFrom(
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getSelectedTableId$),
                this.store$.select(getMasterUploadFiles$)
            ),
            mergeMap(([_action, orgId, tableId, uploadedFiles]) => {
                if (uploadedFiles?.length > 0) {
                    return of(
                        masterActions.SetMasterUploadFiles({
                            organizationId: orgId,
                            tableId: tableId,
                            uploadedFiles: CommonUtilsService.cloneObject(uploadedFiles),
                        })
                    );
                }
                return this._masters.getMasterUploadFiles({ organizationId: orgId, masterTableId: tableId }).pipe(
                    map((res: any) => {
                        res?.filter(obj => {
                            if (obj?.createOn) {
                                obj.createOn = this._utils.transformDateToLocale(
                                    obj.createOn,
                                    'YYYY-MM-DDTHH:MM:SSZ',
                                    'ddMMMyyyy HH:MM:SS AM',
                                    false
                                );
                            }
                        });
                        res.forEach(detail => {
                            detail['fileName'] = this.getfileName(detail.fileUri);
                            detail['downloadable'] = true;
                        });
                        return masterActions.SetMasterUploadFiles({
                            organizationId: orgId,
                            tableId: tableId,
                            uploadedFiles: res,
                        });
                    }),
                    catchError(errorResponse => of(AlertError({ message: errorResponse?.msg })))
                );
            })
        );
    });

    GetMasterRecordChangeLogs$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(masterActions.GetMasterRecordChangeLogs),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$), this.store$.select(getSelectedRestApiName$)),
            mergeMap(([action, orgId, restApiName]) => {
                return this._masters
                    .getMasterRecordChangeLogsObservable({
                        orgId,
                        masterRestApiName: restApiName,
                        keyValuePairs: action?.keyvalues,
                    })
                    .pipe(
                        map((res: ChangeLog[]) => {
                            return masterActions.SetMasterRecordChangeLogs({ changeLogs: res });
                        }),
                        catchError(errorResponse => of(AlertError({ message: errorResponse?.msg })))
                    );
            })
        );
    });
}
